import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, Typography } from 'antd';

import { ProductGroup } from '@/features/constructorProducts';
import { QuestionnaireGroup } from '@/features/constructor';
import { sortWithNulls } from '@/utils/helpers';
import { useParams } from 'react-router';
import { ConstructorApi as ConstructorQuestionnaireApi } from '@/features/constructor/api';
import { ConstructorApi as ConstructorProductsApi } from '@/features/constructorProducts/api';
import { queryClient } from '@/app/QueryClientProvider';
import { adminLocalizationApi, adminLocalizationKeys } from '@/api';
import { SortableItem, SortableList } from '@/components';
import { Container } from '@/ui';
import { StyledPanel } from '@/features/constructorCommon/styled';
import { usePopups } from '@/hooks';

export const GroupList = ({ list, type, ...props }) => {
  const [groups, setGroups] = useState([]);
  const { id } = useParams();
  const { showSuccessToast, showErrorToast } = usePopups();
  const { t } = useTranslation();

  async function updateGroupOrder(groupId, order) {
    const initialGroups = [...groups];

    try {
      if (type === 'questionnaire') {
        await ConstructorQuestionnaireApi.updateGroupOrder({
          id: groupId,
          sortOrder: order.toString()
        });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getQuestionnaireSections],
          queryFn: () =>
            adminLocalizationApi.getQuestionnaireSections({ questionSectionTypeId: id })
        });
      }
      if (type === 'products') {
        await ConstructorProductsApi.updateGroupOrder({ id: groupId, sortOrder: order.toString() });
        await queryClient.refetchQueries({
          queryKey: [adminLocalizationKeys.getProductsSections],
          queryFn: () => adminLocalizationApi.getProductsSections({ productId: id })
        });
      }
      showSuccessToast('Group order is changed');
    } catch (e) {
      setGroups(initialGroups);
      showErrorToast(e, true);
    }
  }

  useEffect(() => {
    if (list) {
      setGroups(
        list?.sort((a, b) => {
          return sortWithNulls(Number(a.sortOrder), Number(b.sortOrder));
        })
      );
    }
  }, [list]);

  return (
    <>
      <Container>
        <Typography.Title level={4}>{t('Groups')}</Typography.Title>
      </Container>

      {groups && type === 'questionnaire' && (
        <div style={{ margin: '16px 0' }}>
          <SortableList list={groups} setList={setGroups} updateOrder={updateGroupOrder}>
            {groups.map((group, index) => (
              <SortableItem
                key={group.id}
                id={group.id}
                handle={true}
                value={group.id}
                isLast={groups.length - 1 === index}
                isFirst={index === 0}
              >
                <Collapse
                  bordered={false}
                  items={[
                    {
                      key: group.id,
                      label: group.title,
                      children: <QuestionnaireGroup groupProp={group} {...props} />
                    }
                  ]}
                />
              </SortableItem>
            ))}
          </SortableList>
        </div>
      )}

      {type === 'products' && groups && (
        <div style={{ margin: '16px 0' }}>
          <Collapse accordion>
            {groups.map((group) => (
              <StyledPanel header={group.title} key={group.id}>
                <ProductGroup groupProp={group} />
              </StyledPanel>
            ))}
          </Collapse>
        </div>
      )}
    </>
  );
};
